import type { AdminUserWithNurses } from "@dutyon/apis";
import { findOneUser, queryKeys, removeUser, updateUserAdminStatus } from "@dutyon/apis";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  getPositionName,
  Loader,
  modals,
  Paper,
  RoleBadge,
  Stack,
  Table,
  Text,
  Title,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronLeft, Mail, Phone, Shield, ShieldCheck, ShieldOff, Trash } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

export function UserDetailPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const id = routeId ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: user, isLoading } = useQuery<AdminUserWithNurses>({
    queryKey: queryKeys.users.detail(id),
    queryFn: async () => {
      const user = await findOneUser(id);
      if (!user) throw new Error("사용자 정보를 찾을 수 없습니다.");
      return user;
    },
    enabled: Boolean(id),
  });

  const deleteMutation = useMutation({
    mutationFn: () => removeUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      navigate("/users");
    },
  });

  const adminStatusMutation = useMutation({
    mutationFn: (isAdmin: boolean) => updateUserAdminStatus({ id, isAdmin }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });

  const handleDelete = () => {
    modals.openConfirmModal({
      title: "사용자 삭제",
      centered: true,
      children: (
        <Text size="sm">정말로 이 사용자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</Text>
      ),
      labels: { confirm: "삭제", cancel: "취소" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(),
    });
  };

  const handleGrantAdmin = () => {
    modals.openConfirmModal({
      title: "관리자 권한 부여",
      centered: true,
      children: (
        <Stack gap="sm">
          <Text size="sm">
            <strong>{user?.name}</strong> 사용자에게 관리자 권한을 부여하시겠습니까?
          </Text>
          <Text size="xs" c="dimmed">
            관리자 권한을 가진 사용자는 시스템의 중요한 기능에 접근할 수 있습니다.
          </Text>
        </Stack>
      ),
      labels: { confirm: "권한 부여", cancel: "취소" },
      confirmProps: { color: "blue" },
      onConfirm: () => adminStatusMutation.mutate(true),
    });
  };

  const handleRevokeAdmin = () => {
    modals.openConfirmModal({
      title: "관리자 권한 제거",
      centered: true,
      children: (
        <Stack gap="sm">
          <Text size="sm">
            <strong>{user?.name}</strong> 사용자의 관리자 권한을 제거하시겠습니까?
          </Text>
          <Text size="xs" c="dimmed">
            권한이 제거되면 관리자 기능에 접근할 수 없게 됩니다.
          </Text>
        </Stack>
      ),
      labels: { confirm: "권한 제거", cancel: "취소" },
      confirmProps: { color: "orange" },
      onConfirm: () => adminStatusMutation.mutate(false),
    });
  };

  const getProvider = (user: AdminUserWithNurses) => {
    if (user.authProvider === "APPLE") return "Apple";
    if (user.authProvider === "FIREBASE") return "Firebase 연동";
    return "이메일";
  };

  if (isLoading) {
    return (
      <TitleContainer
        size="lg"
        title={
          <Group gap="xs">
            <ActionIcon variant="subtle" color="gray" onClick={() => navigate("/users")}>
              <ChevronLeft />
            </ActionIcon>
            <Title order={3}>사용자 상세 정보</Title>
          </Group>
        }
      >
        <Group justify="center" py="xl">
          <Stack align="center" gap="xs">
            <Loader size="md" />
            <Text size="sm" c="dimmed">
              사용자 정보를 불러오는 중입니다...
            </Text>
          </Stack>
        </Group>
      </TitleContainer>
    );
  }

  if (!user) {
    return (
      <TitleContainer
        size="lg"
        title={
          <Group gap="xs">
            <ActionIcon variant="subtle" color="gray" onClick={() => navigate("/users")}>
              <ChevronLeft />
            </ActionIcon>
            <Title order={3}>사용자 상세 정보</Title>
          </Group>
        }
      >
        <Text>존재하지 않는 사용자입니다.</Text>
      </TitleContainer>
    );
  }

  return (
    <TitleContainer
      size="lg"
      title={
        <Group gap="xs">
          <ActionIcon variant="subtle" color="gray" onClick={() => navigate("/users")}>
            <ChevronLeft />
          </ActionIcon>
          <Title order={3}>사용자 상세 정보</Title>
        </Group>
      }
    >
      <Grid gutter="lg">
        {/* 기본 정보 카드 */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="lg">
            <Card withBorder p="xl" radius="md">
              <Stack align="center" gap="md">
                <Avatar src={user.profileImage} size={120} radius={120} />
                <Stack align="center" gap={4}>
                  <Title order={3}>{user.name}</Title>
                </Stack>
              </Stack>

              <Divider my="xl" />

              <Stack gap="md">
                <Group gap="xs">
                  <Mail size={18} className="text-gray-500" />
                  <Text size="sm">{user.email}</Text>
                </Group>
                <Group gap="xs">
                  <Phone size={18} className="text-gray-500" />
                  <Text size="sm">{user.phone || "-"}</Text>
                </Group>
                <Group gap="xs">
                  <Shield size={18} className="text-gray-500" />
                  <Text size="sm">로그인 유형: {getProvider(user)}</Text>
                </Group>
                <Group gap="xs">
                  <ShieldCheck size={18} className="text-gray-500" />
                  <Text size="sm">관리자 권한:</Text>
                  <Badge color={user.isAdmin ? "blue" : "gray"} size="sm">
                    {user.isAdmin ? "활성" : "비활성"}
                  </Badge>
                </Group>
              </Stack>
            </Card>

            <Card withBorder p="lg" radius="md">
              <Title order={5} mb="sm" c="red">
                Danger Zone
              </Title>

              <Stack gap="md">
                {/* Admin Permission Section */}
                <div>
                  <Text size="xs" c="dimmed" mb="sm">
                    관리자 권한을 부여하거나 제거할 수 있습니다.
                  </Text>
                  {user.isAdmin ? (
                    <Button
                      variant="outline"
                      color="orange"
                      fullWidth
                      leftSection={<ShieldOff size={16} />}
                      onClick={handleRevokeAdmin}
                      loading={adminStatusMutation.isPending}
                    >
                      관리자 권한 제거
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      color="blue"
                      fullWidth
                      leftSection={<ShieldCheck size={16} />}
                      onClick={handleGrantAdmin}
                      loading={adminStatusMutation.isPending}
                    >
                      관리자 권한 부여
                    </Button>
                  )}
                </div>

                <Divider />

                {/* Delete User Section */}
                <div>
                  <Text size="xs" c="dimmed" mb="sm">
                    사용자를 삭제하면 모든 관련 데이터가 영구적으로 삭제됩니다.
                  </Text>
                  <Button
                    variant="outline"
                    color="red"
                    fullWidth
                    leftSection={<Trash size={16} />}
                    onClick={handleDelete}
                    loading={deleteMutation.isPending}
                  >
                    사용자 삭제
                  </Button>
                </div>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>

        {/* 상세 정보 및 히스토리 */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="lg">
            <Paper withBorder p="xl" radius="md">
              <Title order={4} mb="lg">
                소속 정보 ({user.nurses?.length || 0})
              </Title>

              {user.nurses && user.nurses.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>병원</Table.Th>
                      <Table.Th>팀</Table.Th>
                      <Table.Th>직책</Table.Th>
                      <Table.Th>가입일</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {user.nurses.map((nurse) => (
                      <Table.Tr key={nurse.id}>
                        <Table.Td>{nurse.company?.name}</Table.Td>
                        <Table.Td>{nurse.team?.name || "-"}</Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Text size="sm">{getPositionName(nurse.position)}</Text>
                            <RoleBadge role={nurse.role} />
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          {nurse.joinDate ? dayjs(nurse.joinDate).format("YYYY-MM-DD") : "-"}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed" size="sm" ta="center" py="xl">
                  소속된 정보가 없습니다.
                </Text>
              )}
            </Paper>

            <Paper withBorder p="xl" radius="md">
              <Title order={4} mb="lg">
                시스템 정보
              </Title>
              <Grid>
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed">
                    사용자 ID
                  </Text>
                  <Text size="sm" fw={500}>
                    {user.id}
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed">
                    가입일시
                  </Text>
                  <Text size="sm" fw={500}>
                    {dayjs(user.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed">
                    최근 수정일시
                  </Text>
                  <Text size="sm" fw={500}>
                    {dayjs(user.updatedAt).format("YYYY-MM-DD HH:mm:ss")}
                  </Text>
                </Grid.Col>
              </Grid>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
    </TitleContainer>
  );
}
